import { queryOptions, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { buildFilterExpression, IssueFilter, IssueMode } from 'pages/Desk/issues/shared';
import { buildOrderBy } from 'utils/query_v3';

export type UseQueueIssuesOptions = UseQueryOptions<string[], unknown, string[]>;

export type UseQueueIssuesProps = Readonly<{
    mode: IssueMode;
    filter: IssueFilter;
    sort: DataSourceSort[];
    options?: Partial<UseQueueIssuesOptions>;
}>;

/**
 * Creates query options for fetching issue queue IDs.
 * This provides a reusable, type-safe query definition with consistent caching.
 *
 * @param mode - The issue mode ('machineHealth' or 'hardwareHealth')
 * @param filter - The current filter configuration
 * @param sort - The current sort configuration
 * @param optionOverrides - Optional overrides for query options
 * @returns TanStack Query options object
 */
export function createQueueIssuesQueryOptions(
    mode: IssueMode,
    filter: IssueFilter,
    sort: DataSourceSort[],
    optionOverrides?: Partial<UseQueueIssuesOptions>,
) {
    return queryOptions({
        queryKey: ['queue-issues', mode, filter, sort],
        queryFn: async () => {
            const filterExpr = buildFilterExpression(mode, filter);
            const orderBy = buildOrderBy(sort, { invert: ['Priority'] });

            return mode === 'hardwareHealth'
                ? await sdClient.getHardwareHealthIssuesQuicklist({
                      filter: filterExpr,
                      orderBy,
                  })
                : await sdClient.getMachineHealthIssuesQuicklist({
                      groupIds: filter.hierarchyGroupIds,
                      filter: filterExpr,
                      orderBy,
                  });
        },
        ...optionOverrides,
    });
}

export function useQueueIssues({ mode, filter, sort, options }: UseQueueIssuesProps) {
    return useQuery(createQueueIssuesQueryOptions(mode, filter, sort, options));
}
